/**
 * Admin → Refunds queue (F2).
 *
 * Three tabs:
 *   • Pending — needs approve / reject
 *   • Approved — historical, links to clawback if one exists
 *   • Rejected — historical, with reason
 *
 * Approve modal asks for refund transactionId + admin notes. Reject modal
 * asks for a rejection reason (mandatory). On approve, server auto-creates
 * a clawback proposal — toast surfaces a deep link to the incentive's
 * clawback review block.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    BadgeCheck, XCircle, AlertTriangle, Eye, ChevronRight, Search, IndianRupee, ArrowRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { ModalShell } from './IncentiveDealClosures.jsx';

const TABS = [
    { id: 'pending',  label: 'Pending',  cls: 'bg-amber-100 text-amber-800' },
    { id: 'approved', label: 'Approved', cls: 'bg-emerald-100 text-emerald-800' },
    { id: 'rejected', label: 'Rejected', cls: 'bg-rose-100 text-rose-800' },
];

const REASON_LABEL = {
    client_changed_mind:    'Client changed mind',
    duplicate_payment:      'Duplicate payment',
    service_not_delivered:  'Service not delivered',
    admin_decision:         'Admin decision',
    other:                  'Other',
};

const MODE_LABEL = {
    bank_transfer: 'Bank transfer',
    upi:           'UPI',
    cash:          'Cash',
    cheque:        'Cheque',
};

const inr = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(v || 0));
const fd  = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function AdminRefunds() {
    const [tab, setTab]   = useState('pending');
    const [rows, setRows] = useState([]);
    const [q, setQ]       = useState('');
    const [acting, setActing] = useState(null);  // { row, mode: 'approve' | 'reject' }

    const load = () => api.get('/refunds', { params: { status: tab } }).then(r => setRows(r.data.rows || []));
    useEffect(() => { load(); }, [tab]);

    const filtered = useMemo(() => {
        if (!q.trim()) return rows;
        const rx = new RegExp(q.trim(), 'i');
        return rows.filter(r => rx.test(r.clientName || '') || rx.test(r.submittedBy?.name || ''));
    }, [rows, q]);

    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-2xl font-extrabold">Refunds</h1>
                <p className="text-sm text-ink-500">
                    Approve pending refund requests. Approval automatically marks the targeted
                    payments refunded and queues a clawback proposal on any linked incentive.
                </p>
            </div>

            <div className="flex gap-2 flex-wrap">
                {TABS.map(t => (
                    <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        className={`ag-tab ${tab === t.id ? 'ag-tab-active' : 'ag-tab-inactive'}`}
                    >{t.label}</button>
                ))}
            </div>

            <div className="ag-card p-3 flex items-center gap-2">
                <Search className="h-4 w-4 text-ink-500" />
                <input
                    className="ag-input flex-1"
                    placeholder="Search client or requester…"
                    value={q}
                    onChange={e => setQ(e.target.value)}
                />
                <span className="text-xs text-ink-500">{filtered.length} shown</span>
            </div>

            <div className="ag-card p-0 overflow-x-auto">
                <table className="ag-table">
                    <thead><tr>
                        <th>Requested</th><th>Client</th><th>Requester</th><th>Reason</th><th>Mode</th>
                        <th>Amount</th><th>Targets</th><th>Proof</th><th></th>
                    </tr></thead>
                    <tbody>
                        {filtered.map(row => {
                            const r = row.refundRequest;
                            return (
                                <tr key={row._id}>
                                    <td className="text-xs text-ink-500">{fd(r.requestedAt)}</td>
                                    <td className="font-medium">
                                        <Link to={`/admin/clients/${row.submissionId}`} className="hover:text-brand-teal-700">
                                            {row.clientName}
                                        </Link>
                                    </td>
                                    <td className="text-xs">{r.requestedBy?.name || '—'}</td>
                                    <td className="text-xs">
                                        <div className="font-semibold">{REASON_LABEL[r.reasonCode] || r.reasonCode}</div>
                                        <div className="text-ink-500 text-[11px] truncate max-w-[200px]">{r.reason}</div>
                                    </td>
                                    <td className="text-xs">{MODE_LABEL[r.refundMode] || r.refundMode}</td>
                                    <td className="tabular-nums font-semibold">{inr(r.amount)}</td>
                                    <td className="text-xs">
                                        {(r.targets || []).map((t, i) => (
                                            <div key={i} className="text-[11px]">
                                                {t.paymentType === 'token'
                                                    ? 'Token'
                                                    : t.paymentType === 'adjustment'
                                                        ? 'Custom amount'
                                                        : `Inst ${i + 1}`} · {inr(t.amount)}
                                            </div>
                                        ))}
                                    </td>
                                    <td>
                                        {r.refundProof?.path ? (
                                            <a href={r.refundProof.path} target="_blank" rel="noreferrer" className="text-brand-teal-700 hover:underline text-xs inline-flex items-center gap-1">
                                                <Eye className="h-3 w-3" /> View
                                            </a>
                                        ) : <span className="text-ink-400 text-xs">—</span>}
                                    </td>
                                    <td className="text-right">
                                        {tab === 'pending' && (
                                            <div className="flex gap-1 justify-end">
                                                <button className="ag-btn-primary text-xs" onClick={() => setActing({ row, mode: 'approve' })}>
                                                    <BadgeCheck className="h-3.5 w-3.5" /> Approve
                                                </button>
                                                <button className="ag-btn-ghost text-xs text-brand-red" onClick={() => setActing({ row, mode: 'reject' })}>
                                                    <XCircle className="h-3.5 w-3.5" /> Reject
                                                </button>
                                            </div>
                                        )}
                                        {tab === 'approved' && r.clawbackOn && (
                                            <Link to={`/admin/incentives/closures/${r.clawbackOn}`} className="ag-btn-ghost text-xs inline-flex items-center gap-1">
                                                Clawback <ChevronRight className="h-3 w-3" />
                                            </Link>
                                        )}
                                        {tab === 'rejected' && r.rejectionReason && (
                                            <span className="text-[11px] text-rose-700 italic max-w-[200px] inline-block truncate" title={r.rejectionReason}>
                                                "{r.rejectionReason}"
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {filtered.length === 0 && (
                            <tr><td colSpan={9} className="text-center text-ink-500 py-10">Nothing here.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {acting && (
                <ActionModal
                    row={acting.row}
                    mode={acting.mode}
                    onClose={(refreshed) => {
                        setActing(null);
                        if (refreshed) load();
                    }}
                />
            )}
        </div>
    );
}

function ActionModal({ row, mode, onClose }) {
    const r = row.refundRequest;
    const [reason, setReason]         = useState('');
    const [txid, setTxid]             = useState('');
    const [adminNotes, setAdminNotes] = useState('');
    const [busy, setBusy]             = useState(false);

    const submit = async () => {
        setBusy(true);
        try {
            if (mode === 'approve') {
                const { data } = await api.post(`/refunds/${row.submissionId}/${r._id}/approve`, {
                    transactionId: txid.trim(),
                    adminNotes:    adminNotes.trim(),
                });
                const promoMsg = data.promotedToToken
                    ? ` Next instalment (₹${Number(data.promotedToToken.amount).toLocaleString('en-IN')}) promoted to token.`
                    : '';
                if (data.clawbackOn) {
                    toast.success((t) => (
                        <span>
                            Refund approved.{promoMsg}{' '}
                            <Link to={`/admin/incentives/closures/${data.clawbackOn}`}
                                  onClick={() => toast.dismiss(t.id)}
                                  className="underline font-semibold">
                                Open clawback <ArrowRight className="h-3 w-3 inline" />
                            </Link>
                        </span>
                    ), { duration: 6000 });
                } else {
                    toast.success(`Refund approved.${promoMsg}`);
                }
            } else {
                if (!reason.trim()) { toast.error('Reason required'); setBusy(false); return; }
                await api.post(`/refunds/${row.submissionId}/${r._id}/reject`, { reason: reason.trim() });
                toast.success('Refund rejected');
            }
            onClose(true);
        } catch {/* toasted */}
        finally { setBusy(false); }
    };

    return (
        <ModalShell
            title={mode === 'approve' ? 'Approve refund' : 'Reject refund'}
            onClose={() => onClose(false)}
            width="max-w-md"
        >
            <div className="bg-ink-50 rounded-lg p-3 text-sm">
                <div className="flex justify-between"><span className="text-ink-500">Client</span><span className="font-medium">{row.clientName}</span></div>
                <div className="flex justify-between"><span className="text-ink-500">Amount</span><span className="font-bold tabular-nums">{inr(r.amount)}</span></div>
                <div className="flex justify-between"><span className="text-ink-500">Mode</span><span>{MODE_LABEL[r.refundMode]}</span></div>
                <div className="flex justify-between"><span className="text-ink-500">Reason</span><span>{REASON_LABEL[r.reasonCode]}</span></div>
            </div>

            {mode === 'approve' ? (
                <>
                    <p className="text-[11px] text-ink-500">
                        Approving will mark the targeted payments refunded and queue a clawback proposal
                        on any linked incentive. You can still adjust the clawback amounts before they finalise.
                    </p>
                    <div>
                        <label className="ag-label">Refund transaction ID (optional)</label>
                        <input className="ag-input" placeholder="e.g. NEFT-2026-04-29-12345" value={txid} onChange={e => setTxid(e.target.value)} />
                    </div>
                    <div>
                        <label className="ag-label">Admin notes (optional)</label>
                        <textarea className="ag-input min-h-[60px]" value={adminNotes} onChange={e => setAdminNotes(e.target.value)} />
                    </div>
                </>
            ) : (
                <>
                    <div className="rounded-lg p-2 text-xs bg-amber-50 text-amber-800 ring-1 ring-amber-200 inline-flex items-start gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />
                        The sales user will be notified with your reason. Be specific.
                    </div>
                    <div>
                        <label className="ag-label">Rejection reason *</label>
                        <textarea className="ag-input min-h-[80px]" required value={reason} onChange={e => setReason(e.target.value)} />
                    </div>
                </>
            )}

            <div className="flex justify-end gap-2 pt-2">
                <button className="ag-btn-ghost" onClick={() => onClose(false)}>Cancel</button>
                <button
                    className={mode === 'approve' ? 'ag-btn-primary' : 'ag-btn-danger'}
                    disabled={busy || (mode === 'reject' && !reason.trim())}
                    onClick={submit}
                >
                    {busy ? 'Saving…' : mode === 'approve' ? 'Approve refund' : 'Reject refund'}
                </button>
            </div>
        </ModalShell>
    );
}
